/**
 * =============================================================================
 * ROASTBREW - MANAGE SUBSCRIPTION PAGE
 * =============================================================================
 * 
 * Luxury subscription management interface with tier selection and billing.
 * 
 * CONNECTED TO:
 * - AuthContext: Reads currentTier, cancelAtPeriodEnd, currentPeriodEnd,
 *                api, syncSubscription from context
 * - Stripe: POST /billing/create-embedded-checkout, /change-plan, /cancel,
 *           /resume, GET /preview-proration
 * - config.js: Uses TIER_COLORS and TIERS for configuration
 * 
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmbeddedCheckoutProvider, EmbeddedCheckout, Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from './AuthContext';
import { TIER_COLORS, TIERS, isLightTheme, tierRank } from './config';
import stripePromise from './stripe';

// =============================================================================
// ADD CARD FORM — must be a child of <Elements> to use Stripe hooks
// =============================================================================

function AddCardForm({ clientSecret, onSuccess, onCancel, themeColors }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setProcessing(true);
    setError('');

    try {
      const cardNumber = elements.getElement(CardNumberElement);
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        { payment_method: { card: cardNumber } }
      );

      if (stripeError) {
        setError(stripeError.message);
      } else if (setupIntent?.payment_method) {
        onSuccess(typeof setupIntent.payment_method === 'string' ? setupIntent.payment_method : setupIntent.payment_method.id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const elementStyle = {
    base: {
      fontSize: '15px',
      fontFamily: '"DM Sans", -apple-system, sans-serif',
      color: themeColors.text,
      letterSpacing: '0.02em',
      '::placeholder': { color: themeColors.muted + '80' },
      iconColor: themeColors.accent,
    },
    invalid: { color: '#E24B4A', iconColor: '#E24B4A' },
  };

  const fieldStyle = {
    padding: '14px 16px', borderRadius: '10px',
    background: themeColors.text === '#F5F5F0' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
    border: `1px solid ${themeColors.muted}25`,
    transition: 'border-color 0.2s ease',
  };

  return (
    <div>
      <p style={{ fontSize: '13px', fontWeight: '500', color: themeColors.muted, fontFamily: '"DM Sans", sans-serif', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        Card Details
      </p>
      {/* Row 1: Card Number */}
      <div style={{ ...fieldStyle, marginBottom: '8px' }}>
        <CardNumberElement options={{ style: elementStyle, showIcon: true }} />
      </div>
      {/* Row 2: Expiry + CVC */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        <div style={fieldStyle}>
          <CardExpiryElement options={{ style: elementStyle }} />
        </div>
        <div style={fieldStyle}>
          <CardCvcElement options={{ style: elementStyle }} />
        </div>
      </div>
      {error && (
        <p style={{ fontSize: '13px', color: '#E24B4A', fontFamily: '"DM Sans", sans-serif', marginTop: '8px', marginBottom: '4px' }}>{error}</p>
      )}
      <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
        {onCancel && (
          <button onClick={onCancel} disabled={processing}
            style={{
              flex: 1, padding: '13px', fontSize: '13px', fontWeight: '500', fontFamily: '"DM Sans", sans-serif',
              letterSpacing: '0.04em', background: 'transparent',
              border: `1px solid ${themeColors.muted}30`, borderRadius: '8px',
              color: themeColors.muted, cursor: 'pointer', transition: 'all 0.2s ease',
            }}>
            Cancel
          </button>
        )}
        <button onClick={handleSubmit} disabled={processing || !stripe}
          style={{
            flex: 1, padding: '13px', fontSize: '13px', fontWeight: '500', fontFamily: '"DM Sans", sans-serif',
            letterSpacing: '0.04em', background: themeColors.accent, border: 'none', borderRadius: '8px',
            color: '#1A1A1A', cursor: processing ? 'wait' : 'pointer', transition: 'all 0.2s ease',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            opacity: processing ? 0.7 : 1,
          }}>
          {processing ? (
            <><span style={{ width: '14px', height: '14px', border: '2px solid transparent', borderTopColor: 'currentColor', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />Saving...</>
          ) : (onCancel ? 'Add Card' : 'Update Card')}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PLAN VISIBILITY — a VIEW decision, kept in the view.
// ---------------------------------------------------------------------------
// Tiers listed here are not offered yet and are hidden from the plan grid.
//
// This is deliberately NOT a flag in config.js. config.js defines what a tier IS
// and what it gates; whether it is currently BUYABLE is a product decision that
// belongs with the page that sells it.
//
// It also must never become a deletion from TIERS: tierRank() returns 0 for an
// unknown key, so removing pro_plus there would make isLocked('pro_plus', …)
// evaluate false and silently unlock every Pro+ lesson to everyone. The tier
// stays fully defined; only this grid changes.
//
// TO LAUNCH A TIER: remove it from this array, after confirming every feature
// listed for it in config.js is real.
const HIDDEN_TIERS = ['pro_plus'];

// The user's own tier is always shown even if hidden, so a subscriber on a
// retired tier still sees their plan marked current rather than watching it
// vanish with a downgrade button where it used to be.
const gridTiers = (currentTier) =>
  Object.entries(TIERS)
    .filter(([key]) => !HIDDEN_TIERS.includes(key) || key === currentTier)
    .sort((a, b) => a[1].rank - b[1].rank);

const derivePageState = (currentTier, subscriptionStatus, cancelAtPeriodEnd, pendingDowngradeTier) => {
  if (tierRank(currentTier) === 0) return 'FREE';
  const tierSuffix = '_' + currentTier.toUpperCase();
  if (subscriptionStatus === 'past_due') return 'PAYMENT_FAILED' + tierSuffix;
  if (cancelAtPeriodEnd) return 'PENDING_CANCELLATION' + tierSuffix;
  if (pendingDowngradeTier) return 'PENDING_DOWNGRADE_PRO_PLUS';
  return 'ACTIVE' + tierSuffix;
};

export default function ManageSubscription() {
  const navigate = useNavigate();
  const { profile, api, syncSubscription } = useAuth();
  const currentTier = profile?.currentTier || 'free';
  const subscriptionStatus = profile?.subscription?.status || null;
  const cancelAtPeriodEnd = profile?.subscription?.cancelAtPeriodEnd ?? false;
  const currentPeriodEnd = profile?.subscription?.currentPeriodEnd ?? null;
  const pendingDowngradeTier = profile?.subscription?.pendingDowngradeTier ?? null;
  const downgradeDate = profile?.subscription?.downgradeDate ?? null;
  // ---------------------------------------------------------------------------
  // THEME COLORS
  // ---------------------------------------------------------------------------
  const themeColors = TIER_COLORS[currentTier];
  const pageState = derivePageState(currentTier, subscriptionStatus, cancelAtPeriodEnd, pendingDowngradeTier);

  // ---------------------------------------------------------------------------
  // LOCAL STATE
  // ---------------------------------------------------------------------------
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingTier, setPendingTier] = useState(null);
  const [prices, setPrices] = useState(null);
  const [error, setError] = useState('');
  const [checkoutClientSecret, setCheckoutClientSecret] = useState(null);

  // CP4: Cancel / Resume
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelProcessing, setCancelProcessing] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [resumeProcessing, setResumeProcessing] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);

  // CP5: Plan switching with proration
  const [showProrationModal, setShowProrationModal] = useState(false);
  const [prorationData, setProrationData] = useState(null);
  const [prorationLoading, setProrationLoading] = useState(false);
  const [prorationError, setProrationError] = useState('');
  const [planChangeProcessing, setPlanChangeProcessing] = useState(false);
  const [prorationNotice, setProrationNotice] = useState('');
  const [scheduledChangeBlocked, setScheduledChangeBlocked] = useState(false);

  // Payment method modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);
  const [setupClientSecret, setSetupClientSecret] = useState(null);
  const [paymentMethodError, setPaymentMethodError] = useState('');

  // Payment failed inline card form
  const [failedSetupSecret, setFailedSetupSecret] = useState(null);
  const [failedCardSaving, setFailedCardSaving] = useState(false);
  const [failedCardError, setFailedCardError] = useState('');

  // Downgrade
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [downgradeProcessing, setDowngradeProcessing] = useState(false);
  const [downgradeError, setDowngradeError] = useState('');
  const [resumeBeforeDowngrade, setResumeBeforeDowngrade] = useState(false);
  const [cancelDowngradeProcessing, setCancelDowngradeProcessing] = useState(false);

  // ---------------------------------------------------------------------------
  // HELPERS
  // ---------------------------------------------------------------------------

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatAmount = (cents, currency) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'usd' }).format(cents / 100);
  };

  // ---------------------------------------------------------------------------
  // FETCH PRICES + SYNC ON MOUNT
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const loadPrices = async () => {
      try {
        const res = await api.fetch('/billing/prices');
        if (res.ok) {
          const data = await res.json();
          setPrices(data.prices);
        }
      } catch (err) {
        console.error('Failed to load prices:', err);
      }
    };
    loadPrices();

    // Sync subscription state for paying users on mount
    if (tierRank(currentTier) > 0) {
      syncSubscription();
    }
  }, [api, currentTier, syncSubscription]);

  // Auto-fetch setup intent for payment failed state
  useEffect(() => {
    if (!pageState.startsWith('PAYMENT_FAILED')) return;
    const loadSetupIntent = async () => {
      try {
        const res = await api.fetch('/billing/create-setup-intent', { method: 'POST' });
        if (res.ok) {
          const data = await res.json();
          setFailedSetupSecret(data.clientSecret);
        }
      } catch (err) {
        console.error('Failed to load setup intent:', err);
      }
    };
    loadSetupIntent();
  }, [pageState, api]);

  // ---------------------------------------------------------------------------
  // EVENT HANDLERS
  // ---------------------------------------------------------------------------
  
  /** Route tier selection to the correct flow */
  const handleTierSelect = (tier) => {
    if (tier === currentTier) return;
    const isUpgrade = tierRank(tier) > tierRank(currentTier);
    const isDowngrade = tierRank(tier) < tierRank(currentTier);

    setPendingTier(tier);
    setError('');
    setProrationError('');
    setDowngradeError('');

    if (tierRank(currentTier) === 0) {
      // Free → paid: embedded checkout
      setShowConfirmModal(true);
    } else if (pageState.startsWith('ACTIVE')) {
      if (isUpgrade) {
        // Paid → higher paid: proration preview
        setShowProrationModal(true);
        fetchProration(tier);
      } else if (isDowngrade && tierRank(tier) === 0) {
        // Paid → free: cancel flow
        setShowCancelModal(true);
      } else if (isDowngrade && tierRank(tier) > 0) {
        // Pro+ → Pro: schedule downgrade
        setResumeBeforeDowngrade(false);
        setShowDowngradeModal(true);
      }
    } else if (pageState === 'PENDING_DOWNGRADE_PRO_PLUS') {
      if (isDowngrade && tierRank(tier) === 0) {
        // Pending downgrade → cancel: release schedule + cancel
        setShowCancelModal(true);
      }
    } else if (pageState.startsWith('PENDING_CANCELLATION')) {
      if (isUpgrade) {
        // Pending cancellation → upgrade: resume first, then proration
        setShowProrationModal(true);
        handleResumeAndProration(tier);
      } else if (isDowngrade && tierRank(tier) > 0) {
        // Pending cancellation → downgrade: resume first, then schedule
        setResumeBeforeDowngrade(true);
        setShowDowngradeModal(true);
      }
    }
  };

  /**
   * Load the upgrade price. The answer includes prorationDate, the moment it was priced; the upgrade is charged as
   * of that moment. A scheduled downgrade gets its own message and button instead of an error.
   */
  const loadPreview = async (tier) => {
    // Plans, never price ids: the server uses the plan's current price in Stripe.
    if (!prices?.[tier]) throw new Error('Pricing not available');

    const res = await api.fetch(`/billing/preview-proration?plan=${encodeURIComponent(tier)}`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (data.code === 'SCHEDULED_CHANGE') {
        setScheduledChangeBlocked(true);
        return;
      }
      throw new Error(data.error || 'Failed to load proration preview');
    }
    setProrationData(data);
  };

  /** Fetch proration preview for plan switch */
  const fetchProration = async (tier) => {
    setProrationLoading(true);
    setProrationData(null);
    setProrationError('');
    setProrationNotice('');
    setScheduledChangeBlocked(false);

    try {
      await loadPreview(tier);
    } catch (err) {
      setProrationError(err.message);
    } finally {
      setProrationLoading(false);
    }
  };

  /** Process checkout for free → paid (CP3) */
  const confirmChange = async () => {
    setIsProcessing(true);
    setError('');

    try {
      if (tierRank(currentTier) === 0) {
        // First-time subscription — Embedded Checkout
        if (!prices?.[pendingTier]) throw new Error('Pricing not available. Please try again.');

        const res = await api.fetch('/billing/create-embedded-checkout', {
          method: 'POST',
          body: JSON.stringify({ plan: pendingTier }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          const failure = new Error(data.error || 'Failed to create checkout session');
          failure.status = res.status;
          throw failure;
        }

        const { clientSecret } = await res.json();
        setCheckoutClientSecret(clientSecret);
        setShowConfirmModal(false);
        setIsProcessing(false);
      } else {
        // Shouldn't reach here — paid users use proration or cancel modals
        setIsProcessing(false);
        setShowConfirmModal(false);
      }
    } catch (err) {
      // 409: this account already has a subscription (for example this tab is out of date). Just reload the page,
      // so it shows the real plan. The sync first copies Stripe's state into the database, so the reloaded page is
      // right even if Stripe's webhook hasn't arrived yet (it never throws). Any other error stays in the dialog.
      if (err.status === 409) {
        await syncSubscription();
        window.location.reload();
        return;
      }
      setError(err.message);
      setIsProcessing(false);
    }
  };

  /** Cancel subscription (CP4) */
  const handleCancel = async () => {
    setCancelProcessing(true);
    setCancelError('');

    try {
      const res = await api.fetch('/billing/cancel', { method: 'POST' });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to cancel subscription');
      }

      await syncSubscription();
      setShowCancelModal(false);
    } catch (err) {
      setCancelError(err.message);
    } finally {
      setCancelProcessing(false);
    }
  };

  /** Resume subscription (CP4) */
  const handleResume = async () => {
    setResumeProcessing(true);

    try {
      const res = await api.fetch('/billing/resume', { method: 'POST' });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to resume subscription');
      }

      await syncSubscription();
    } catch (err) {
      console.error('Resume failed:', err);
    } finally {
      setResumeProcessing(false);
    }
  };

  /** Resume subscription then fetch proration preview (for upgrading during cancellation) */
  const handleResumeAndProration = async (tier) => {
    setProrationLoading(true);
    setProrationData(null);
    setProrationError('');
    setProrationNotice('');
    setScheduledChangeBlocked(false);

    try {
      // Resume first
      const resumeRes = await api.fetch('/billing/resume', { method: 'POST' });
      if (!resumeRes.ok) {
        const resumeData = await resumeRes.json().catch(() => ({}));
        throw new Error(resumeData.error || 'Failed to resume subscription');
      }

      // Fetch proration preview
      await loadPreview(tier);
      await syncSubscription();
    } catch (err) {
      setProrationError(err.message);
    } finally {
      setProrationLoading(false);
    }
  };

  /** Confirm plan switch (CP5) */
  const handlePlanChange = async () => {
    setPlanChangeProcessing(true);
    setProrationError('');
    setProrationNotice('');

    try {
      if (!prices?.[pendingTier]) throw new Error('Pricing not available');

      const res = await api.fetch('/billing/change-plan', {
        method: 'POST',
        // Charged as of the preview's moment and at the preview's price, so the amount charged equals the amount shown.
        body: JSON.stringify({ plan: pendingTier, prorationDate: prorationData?.prorationDate, priceCheck: prorationData?.priceCheck }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.code === 'PREVIEW_EXPIRED') {
          // The price shown is too old: show a fresh one (today's charge AND the plans' monthly prices); the customer
          // confirms again.
          setProrationData(null);
          setProrationLoading(true);
          try {
            try {
              const pricesRes = await api.fetch('/billing/prices');
              if (pricesRes.ok) {
                const pricesData = await pricesRes.json();
                if (pricesData && pricesData.prices) setPrices(pricesData.prices);
              }
            } catch (pricesErr) {
              console.error('Failed to reload prices:', pricesErr);
            }
            await loadPreview(pendingTier);
            setProrationNotice(data.error);
          } finally {
            setProrationLoading(false);
          }
          return;
        }
        if (data.code === 'SCHEDULED_CHANGE') {
          setProrationData(null);
          setScheduledChangeBlocked(true);
          return;
        }
        throw new Error(data.error || 'Failed to change plan');
      }

      await syncSubscription();
      setShowProrationModal(false);
    } catch (err) {
      setProrationError(err.message);
    } finally {
      setPlanChangeProcessing(false);
    }
  };

  /** Close checkout overlay */
  const handleCloseCheckout = () => {
    setCheckoutClientSecret(null);
  };

  /** Fetch all payment methods on file */
  const fetchPaymentMethods = useCallback(async () => {
    setPaymentMethodsLoading(true);
    setPaymentMethodError('');
    try {
      const res = await api.fetch('/billing/payment-methods');
      if (!res.ok) throw new Error('Failed to load payment methods');
      const data = await res.json();
      setPaymentMethods(data.paymentMethods || []);
    } catch (err) {
      setPaymentMethodError(err.message);
    } finally {
      setPaymentMethodsLoading(false);
    }
  }, [api]);

  /** Open payment method modal */
  const openPaymentModal = async () => {
    setShowPaymentModal(true);
    setShowAddCard(false);
    setSetupClientSecret(null);
    fetchPaymentMethods();
  };

  /** Start add card flow — fetch setup intent */
  const startAddCard = async () => {
    setPaymentMethodError('');
    try {
      const res = await api.fetch('/billing/create-setup-intent', { method: 'POST' });
      if (!res.ok) throw new Error('Failed to initialize card form');
      const data = await res.json();
      setSetupClientSecret(data.clientSecret);
      setShowAddCard(true);
    } catch (err) {
      setPaymentMethodError(err.message);
    }
  };

  /** After card added — set as default and refresh list */
  const handleCardAdded = async (paymentMethodId) => {
    setPaymentMethodError('');
    try {
      const res = await api.fetch('/billing/set-default-payment-method', {
        method: 'POST',
        body: JSON.stringify({ paymentMethodId }),
      });
      if (!res.ok) {
        // 402: the card was saved, but retrying the failed payment with it was declined.
        const data = await res.json().catch(() => ({}));
        if (res.status === 402) {
          setShowAddCard(false);
          setSetupClientSecret(null);
          fetchPaymentMethods();
        }
        throw new Error(data.error || 'Failed to set default payment method');
      }
      setShowAddCard(false);
      setSetupClientSecret(null);
      fetchPaymentMethods();
      await syncSubscription(); // a retried payment unlocks the app straight away
    } catch (err) {
      setPaymentMethodError(err.message);
    }
  };

  /** Set a card as default */
  const handleSetDefault = async (paymentMethodId) => {
    setPaymentMethodError('');
    try {
      const res = await api.fetch('/billing/set-default-payment-method', {
        method: 'POST',
        body: JSON.stringify({ paymentMethodId }),
      });
      if (!res.ok) {
        // 402: the card was made the default, but retrying the failed payment with it was declined.
        const data = await res.json().catch(() => ({}));
        if (res.status === 402) fetchPaymentMethods();
        throw new Error(data.error || 'Failed to set default');
      }
      fetchPaymentMethods();
      await syncSubscription(); // a retried payment unlocks the app straight away
    } catch (err) {
      setPaymentMethodError(err.message);
    }
  };

  /** Remove a card */
  const handleRemoveCard = async (paymentMethodId) => {
    setPaymentMethodError('');
    try {
      const res = await api.fetch(`/billing/payment-method/${paymentMethodId}`, { method: 'DELETE' });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to remove card');
      }
      fetchPaymentMethods();
    } catch (err) {
      setPaymentMethodError(err.message);
    }
  };

  /** Handle card save from inline payment failed form */
  const handleFailedCardSave = async (paymentMethodId) => {
    setFailedCardSaving(true);
    setFailedCardError('');
    try {
      const res = await api.fetch('/billing/set-default-payment-method', {
        method: 'POST',
        body: JSON.stringify({ paymentMethodId }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to update payment method');
      }
      await syncSubscription();
    } catch (err) {
      setFailedCardError(err.message);
      // The card setup was already used by this card, so fetch a fresh one: the form reloads empty
      // and another card can be entered straight away.
      setFailedSetupSecret(null);
      try {
        const setupRes = await api.fetch('/billing/create-setup-intent', { method: 'POST' });
        if (setupRes.ok) {
          const setupData = await setupRes.json();
          setFailedSetupSecret(setupData.clientSecret);
        }
      } catch (setupErr) {
        console.error('Failed to load setup intent:', setupErr);
      }
    } finally {
      setFailedCardSaving(false);
    }
  };

  /** Schedule a downgrade (Pro+ → Pro) at period end */
  const handleDowngrade = async () => {
    setDowngradeProcessing(true);
    setDowngradeError('');
    try {
      // Resume cancellation first if needed (coming from PENDING_CANCELLATION)
      if (resumeBeforeDowngrade) {
        const resumeRes = await api.fetch('/billing/resume', { method: 'POST' });
        if (!resumeRes.ok) throw new Error('Failed to resume subscription');
      }

      if (!prices?.[pendingTier]) throw new Error('Pricing not available');

      const res = await api.fetch('/billing/downgrade', {
        method: 'POST',
        body: JSON.stringify({ plan: pendingTier }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to schedule downgrade');
      }

      setResumeBeforeDowngrade(false);
      await syncSubscription();
      setShowDowngradeModal(false);
    } catch (err) {
      setDowngradeError(err.message);
    } finally {
      setDowngradeProcessing(false);
    }
  };

  /** Cancel a scheduled downgrade — keep current plan */
  const handleCancelDowngrade = async () => {
    setCancelDowngradeProcessing(true);
    try {
      const res = await api.fetch('/billing/cancel-downgrade', { method: 'POST' });
      if (!res.ok) throw new Error('Failed to cancel downgrade');
      await syncSubscription();
    } catch (err) {
      console.error('Cancel downgrade failed:', err);
    } finally {
      setCancelDowngradeProcessing(false);
    }
  };

  /** Upgrade dialog: cancel the scheduled downgrade, then show a fresh price. Charges nothing by itself. */
  const handleCancelDowngradeAndContinue = async () => {
    setProrationLoading(true);
    setProrationError('');
    setProrationNotice('');
    try {
      const res = await api.fetch('/billing/cancel-downgrade', { method: 'POST' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to cancel downgrade');
      }
      await syncSubscription();
      setScheduledChangeBlocked(false);
      await loadPreview(pendingTier);
    } catch (err) {
      setProrationError(err.message);
    } finally {
      setProrationLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  
  return (
    <div style={{
      minHeight: '100vh',
      background: themeColors.bg,
      fontFamily: '"Cormorant Garamond", Georgia, serif',
      transition: 'background 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* =================================================================
          AMBIENT BACKGROUND - Dual gradient overlay
          ================================================================= */}
      <div style={{
        position: 'fixed',
        inset: 0,
        background: isLightTheme(currentTier)
          ? 'radial-gradient(ellipse at 30% 0%, rgba(201, 169, 98, 0.06) 0%, transparent 50%), radial-gradient(ellipse at 70% 100%, rgba(201, 169, 98, 0.04) 0%, transparent 50%)'
          : 'radial-gradient(ellipse at 50% 0%, rgba(201, 169, 98, 0.1) 0%, transparent 40%)',
        pointerEvents: 'none',
      }} />

      {/* =================================================================
          HEADER - Back button and current plan badge
          ================================================================= */}
      <header style={{
        padding: '24px 48px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative',
        zIndex: 10,
      }}>
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'none',
            border: 'none',
            color: themeColors.muted,
            fontSize: '14px',
            fontFamily: '"DM Sans", sans-serif',
            cursor: 'pointer',
            padding: '8px 0',
            transition: 'color 0.2s ease',
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = themeColors.text}
          onMouseLeave={(e) => e.currentTarget.style.color = themeColors.muted}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back to Home
        </button>

        {/* Current Plan Badge */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '8px 16px',
          background: isLightTheme(currentTier) 
            ? 'rgba(26, 26, 26, 0.03)' : 'rgba(201, 169, 98, 0.1)',
          border: `1px solid ${themeColors.accent}40`,
          borderRadius: '24px',
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: themeColors.accent,
            boxShadow: !isLightTheme(currentTier) ? `0 0 10px ${themeColors.accent}` : 'none',
          }} />
          <span style={{
            fontSize: '13px',
            fontWeight: '500',
            color: themeColors.text,
            fontFamily: '"DM Sans", sans-serif',
          }}>
            Current: {themeColors.label}
          </span>
        </div>
      </header>

      {/* =================================================================
          MAIN CONTENT
          ================================================================= */}
      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '40px 24px 80px',
        position: 'relative',
        zIndex: 1,
      }}>
        {/* Page Title */}
        <div style={{
          textAlign: 'center',
          marginBottom: '48px',
          animation: 'fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
          <h1 style={{
            fontSize: 'clamp(36px, 5vw, 52px)',
            fontWeight: '300',
            color: themeColors.text,
            marginBottom: '16px',
            letterSpacing: '-0.02em',
          }}>
            Choose your <span style={{ fontStyle: 'italic', fontWeight: '400' }}>experience</span>
          </h1>
          <p style={{
            fontSize: '17px',
            color: themeColors.muted,
            fontFamily: '"DM Sans", sans-serif',
            maxWidth: '500px',
            margin: '0 auto',
            lineHeight: 1.6,
          }}>
            Elevate your journey with a plan tailored to your ambitions
          </p>
        </div>

        {/* =============================================================
            SUBSCRIPTION INFO BOX — normal (ACTIVE / PENDING_CANCELLATION)
            ============================================================= */}
        {pageState !== 'FREE' && !pageState.startsWith('PAYMENT_FAILED') && (
          <div style={{
            marginBottom: '40px',
            padding: '24px 32px',
            borderRadius: '16px',
            background: isLightTheme(currentTier) ? '#FFFFFF' : 'rgba(255, 255, 255, 0.03)',
            border: `1px solid ${isLightTheme(currentTier) ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '16px',
            animation: 'fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.15s both',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <p style={{
                fontSize: '14px',
                color: themeColors.text,
                fontFamily: '"DM Sans", sans-serif',
                lineHeight: 1.5,
              }}>
                Your current plan is <strong>{TIERS[currentTier]?.name}</strong>.{' '}
                {pageState === 'PENDING_DOWNGRADE_PRO_PLUS'
                  ? <>Your plan switches to <strong>{TIERS[pendingDowngradeTier]?.name}</strong> on {formatDate(downgradeDate)}</>
                  : cancelAtPeriodEnd
                    ? <span style={{ color: '#E24B4A' }}>Your subscription ends {formatDate(currentPeriodEnd)}</span>
                    : <>Your subscription renews {formatDate(currentPeriodEnd)}</>
                }
              </p>
            </div>

            <button
              onClick={openPaymentModal}
              style={{
                padding: '10px 20px',
                fontSize: '13px',
                fontWeight: '500',
                fontFamily: '"DM Sans", sans-serif',
                background: 'transparent',
                color: themeColors.text,
                border: `1px solid ${themeColors.muted}40`,
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = themeColors.accent; e.currentTarget.style.color = themeColors.accent; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = themeColors.muted + '40'; e.currentTarget.style.color = themeColors.text; }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                <line x1="1" y1="10" x2="23" y2="10"/>
              </svg>
              Payment Method
            </button>
          </div>
        )}

        {/* =============================================================
            PAYMENT FAILED — expanded info box with inline card form
            ============================================================= */}
        {pageState.startsWith('PAYMENT_FAILED') && (
          <div style={{
            marginBottom: '40px',
            padding: '32px',
            borderRadius: '16px',
            background: 'rgba(226, 75, 74, 0.04)',
            border: '1px solid rgba(226, 75, 74, 0.15)',
            animation: 'fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.15s both',
          }}>
            {/* Warning Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '12px' }}>
              <div style={{
                width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(226, 75, 74, 0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#E24B4A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 8v4M12 16h.01"/>
                </svg>
              </div>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: '400', color: themeColors.text, fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
                  Payment Failed
                </h3>
                <p style={{ fontSize: '13px', color: themeColors.muted, fontFamily: '"DM Sans", sans-serif', marginTop: '2px' }}>
                  Your {TIERS[currentTier]?.name} plan
                </p>
              </div>
            </div>

            {/* Warning Message */}
            <p style={{
              fontSize: '14px', color: themeColors.text, fontFamily: '"DM Sans", sans-serif',
              lineHeight: 1.7, marginBottom: '24px', paddingLeft: '54px',
            }}>
              We were unable to process your payment. Please update your card below to continue your subscription. If not updated, your {TIERS[currentTier]?.name} plan will be canceled automatically.
            </p>

            {/* Divider */}
            <div style={{ height: '1px', background: `linear-gradient(90deg, transparent, rgba(226,75,74,0.15), transparent)`, marginBottom: '24px' }} />

            {/* Inline Card Form */}
            {failedSetupSecret ? (
              <div style={{ maxWidth: '440px' }}>
                <Elements key={failedSetupSecret} stripe={stripePromise} options={{ appearance: { theme: isLightTheme(currentTier) ? 'stripe' : 'night' } }}>
                  <AddCardForm
                    clientSecret={failedSetupSecret}
                    onSuccess={handleFailedCardSave}
                    onCancel={null}
                    themeColors={themeColors}
                  />
                </Elements>
                {failedCardError && (
                  <p style={{ fontSize: '13px', color: '#E24B4A', fontFamily: '"DM Sans", sans-serif', marginTop: '12px' }}>{failedCardError}</p>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ width: '24px', height: '24px', border: '2px solid rgba(128,128,128,0.15)', borderTopColor: themeColors.accent, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
                <p style={{ fontSize: '13px', color: themeColors.muted, fontFamily: '"DM Sans", sans-serif' }}>Loading payment form...</p>
              </div>
            )}
          </div>
        )}

        {/* =============================================================
            TIER CARDS GRID — hidden during payment failure
            ============================================================= */}
        {!pageState.startsWith('PAYMENT_FAILED') && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '24px',
          alignItems: 'start',
        }}>
          {/* gridTiers, not Object.entries(TIERS) — see HIDDEN_TIERS above. */}
          {gridTiers(currentTier).map(([tierKey, tier], index) => {
            const isActive = currentTier === tierKey;
            const tierAccent = TIER_COLORS[tierKey].accent;
            const tierPrice = prices?.[tierKey];
            const displayPrice = tierRank(tierKey) === 0 ? 0 : (tierPrice ? tierPrice.amount / 100 : null);
            const displayInterval = tierRank(tierKey) === 0 ? 'forever' : (tierPrice?.interval || 'month');
            const isUpgrade = tierRank(tierKey) > tierRank(currentTier);

            // Button logic per page state
            let buttonLabel, buttonDisabled;
            if (pageState === 'FREE') {
              buttonLabel = isActive ? 'Current Plan' : 'Subscribe';
              buttonDisabled = isActive;
            } else if (pageState.startsWith('ACTIVE')) {
              if (isActive) {
                buttonLabel = 'Current Plan';
                buttonDisabled = true;
              } else if (isUpgrade) {
                buttonLabel = 'Upgrade';
                buttonDisabled = false;
              } else if (tierRank(tierKey) > 0) {
                buttonLabel = 'Downgrade';
                buttonDisabled = false;
              } else {
                buttonLabel = 'Cancel Plan';
                buttonDisabled = false;
              }
            } else if (pageState === 'PENDING_DOWNGRADE_PRO_PLUS') {
              if (isActive) {
                buttonLabel = 'Keep Pro+';
                buttonDisabled = false;
              } else if (tierKey === pendingDowngradeTier) {
                buttonLabel = `Switching ${formatDate(downgradeDate)}`;
                buttonDisabled = true;
              } else {
                buttonLabel = 'Cancel Plan';
                buttonDisabled = false;
              }
            } else if (pageState.startsWith('PENDING_CANCELLATION')) {
              if (isActive) {
                buttonLabel = 'Resume';
                buttonDisabled = false;
              } else if (isUpgrade) {
                buttonLabel = 'Upgrade';
                buttonDisabled = false;
              } else if (tierRank(tierKey) > 0 && !isUpgrade) {
                buttonLabel = 'Downgrade';
                buttonDisabled = false;
              } else {
                buttonLabel = '';
                buttonDisabled = true;
              }
            } else {
              // PAYMENT_FAILED_*
              buttonLabel = isActive ? 'Current Plan' : (isUpgrade ? 'Upgrade' : '');
              buttonDisabled = true;
            }
            
            return (
              <div
                key={tierKey}
                style={{
                  position: 'relative',
                  animation: `fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${0.15 + index * 0.1}s both`,
                }}
              >
                {/* Badge (Most Popular / Premium) */}
                {tier.badge && (
                  <div style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    padding: '6px 16px',
                    background: tierAccent,
                    color: isLightTheme(tierKey) ? '#F5F5F0' : '#1A1A1A',
                    fontSize: '11px',
                    fontWeight: '600',
                    fontFamily: '"DM Sans", sans-serif',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    borderRadius: '20px',
                    zIndex: 1,
                    boxShadow: `0 4px 16px ${tierAccent}40`,
                  }}>
                    {tier.badge}
                  </div>
                )}

                {/* Card Container */}
                <div 
                  style={{
                    padding: '40px 32px',
                    borderRadius: '16px',
                    background: isActive
                      ? (isLightTheme(currentTier) ? 'rgba(26, 26, 26, 0.02)' : 'rgba(201, 169, 98, 0.08)')
                      : (isLightTheme(currentTier) ? '#FFFFFF' : 'rgba(255, 255, 255, 0.02)'),
                    border: `1px solid ${isActive ? tierAccent : (isLightTheme(currentTier) ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)')}`,
                    transition: 'all 0.4s ease',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                  onMouseEnter={(e) => {
                    if (!buttonDisabled) {
                      e.currentTarget.style.borderColor = tierAccent + '60';
                      e.currentTarget.style.transform = 'translateY(-4px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!buttonDisabled) {
                      e.currentTarget.style.borderColor = isLightTheme(currentTier) ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {/* Active Checkmark Indicator */}
                  {isActive && (
                    <div style={{
                      position: 'absolute',
                      top: '20px',
                      right: '20px',
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: tierAccent,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: `0 4px 12px ${tierAccent}40`,
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M20 6L9 17l-5-5" stroke={isLightTheme(tierKey) ? '#F5F5F0' : '#1A1A1A'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  )}

                  {/* Tier Name */}
                  <h3 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: tierAccent,
                    marginBottom: '8px',
                    fontFamily: '"DM Sans", sans-serif',
                  }}>
                    {tier.name}
                  </h3>

                  {/* Description */}
                  <p style={{
                    fontSize: '15px',
                    color: themeColors.muted,
                    marginBottom: '24px',
                    fontFamily: '"DM Sans", sans-serif',
                  }}>
                    {tier.description}
                  </p>

                  {/* Price Display */}
                  <div style={{
                    marginBottom: '32px',
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '4px',
                  }}>
                    <span style={{ fontSize: '20px', fontWeight: '300', color: themeColors.text }}>$</span>
                    <span style={{ fontSize: '56px', fontWeight: '300', color: themeColors.text, lineHeight: 1, letterSpacing: '-0.02em' }}>
                      {displayPrice !== null ? displayPrice : '—'}
                    </span>
                    <span style={{ fontSize: '14px', color: themeColors.muted, fontFamily: '"DM Sans", sans-serif', marginLeft: '4px' }}>
                      /{displayInterval}
                    </span>
                  </div>

                  {/* Features List */}
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px 0' }}>
                    {tier.features.map((feature, i) => (
                      <li key={i} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        padding: '10px 0',
                        fontSize: '14px',
                        color: themeColors.text,
                        fontFamily: '"DM Sans", sans-serif',
                        borderBottom: i < tier.features.length - 1 
                          ? `1px solid ${isLightTheme(currentTier) ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)'}`
                          : 'none',
                      }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: '2px' }}>
                          <path d="M20 6L9 17l-5-5" stroke={tierAccent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* Limitations (Free tier only) */}
                  {tier.limitations && (
                    <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px 0' }}>
                      {tier.limitations.map((limitation, i) => (
                        <li key={i} style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '12px',
                          padding: '8px 0',
                          fontSize: '13px',
                          color: themeColors.muted,
                          fontFamily: '"DM Sans", sans-serif',
                        }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: '2px' }}>
                            <path d="M18 6L6 18M6 6l12 12" stroke={themeColors.muted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          {limitation}
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Action Button */}
                  <button
                    onClick={() => {
                      if (pageState.startsWith('PENDING_CANCELLATION') && tierKey === currentTier) {
                        setShowResumeModal(true);
                      } else if (pageState === 'PENDING_DOWNGRADE_PRO_PLUS' && tierKey === currentTier) {
                        handleCancelDowngrade();
                      } else {
                        handleTierSelect(tierKey);
                      }
                    }}
                    disabled={buttonDisabled}
                    style={{
                      width: '100%',
                      padding: '16px',
                      fontSize: '14px',
                      fontWeight: '500',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      fontFamily: '"DM Sans", sans-serif',
                      background: buttonDisabled 
                        ? 'transparent'
                        : (tierRank(tierKey) === 1 ? tierAccent : 'transparent'),
                      color: buttonDisabled
                        ? themeColors.muted
                        : (tierRank(tierKey) === 1 ? '#1A1A1A' : themeColors.text),
                      border: `1px solid ${buttonDisabled ? themeColors.muted + '40' : (tierRank(tierKey) === 1 ? tierAccent : themeColors.text + '40')}`,
                      borderRadius: '8px',
                      cursor: buttonDisabled ? 'default' : 'pointer',
                      transition: 'all 0.3s ease',
                      opacity: (!isActive && buttonDisabled) ? 0.5 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (!buttonDisabled) {
                        e.currentTarget.style.background = tierAccent;
                        e.currentTarget.style.borderColor = tierAccent;
                        e.currentTarget.style.color = isLightTheme(tierKey) ? '#F5F5F0' : '#1A1A1A';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!buttonDisabled) {
                        e.currentTarget.style.background = tierRank(tierKey) === 1 ? tierAccent : 'transparent';
                        e.currentTarget.style.borderColor = tierRank(tierKey) === 1 ? tierAccent : themeColors.text + '40';
                        e.currentTarget.style.color = tierRank(tierKey) === 1 ? '#1A1A1A' : themeColors.text;
                      }
                    }}
                  >
                    {buttonLabel}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        )}

        {/* =============================================================
            SUBSCRIPTION STATUS (CP4) — visible for pending cancellation
            ============================================================= */}
        {pageState.startsWith('PENDING_CANCELLATION') && (
          <div style={{
            marginTop: '48px',
            maxWidth: '600px',
            margin: '48px auto 0',
            padding: '32px',
            borderRadius: '16px',
            background: isLightTheme(currentTier) ? '#FFFFFF' : 'rgba(255, 255, 255, 0.03)',
            border: `1px solid ${isLightTheme(currentTier) ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'}`,
            textAlign: 'center',
            animation: 'fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.4s both',
          }}>
            <p style={{
              fontSize: '15px',
              color: themeColors.muted,
              fontFamily: '"DM Sans", sans-serif',
              marginBottom: '20px',
              lineHeight: 1.6,
            }}>
              Your {TIERS[currentTier]?.name} plan ends on {formatDate(currentPeriodEnd)}
            </p>
            <button
              onClick={() => setShowResumeModal(true)}
              disabled={resumeProcessing}
              style={{
                padding: '12px 32px',
                fontSize: '14px',
                fontWeight: '500',
                fontFamily: '"DM Sans", sans-serif',
                background: themeColors.accent,
                color: '#1A1A1A',
                border: 'none',
                borderRadius: '8px',
                cursor: resumeProcessing ? 'wait' : 'pointer',
                transition: 'all 0.2s ease',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {resumeProcessing ? (
                <>
                  <span style={{ width: '14px', height: '14px', border: '2px solid transparent', borderTopColor: 'currentColor', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                  Resuming...
                </>
              ) : 'Resume Plan'}
            </button>
          </div>
        )}

        {/* =============================================================
            FOOTER - Contact link
            ============================================================= */}
        <div style={{
          marginTop: '80px',
          textAlign: 'center',
          animation: 'fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.5s both',
        }}>
          <p style={{
            fontSize: '14px',
            color: themeColors.muted,
            fontFamily: '"DM Sans", sans-serif',
            marginBottom: '8px',
          }}>
            Questions about our plans?
          </p>
          <a 
            href="#" 
            style={{
              fontSize: '14px',
              color: themeColors.accent,
              fontFamily: '"DM Sans", sans-serif',
              textDecoration: 'none',
              borderBottom: `1px solid ${themeColors.accent}40`,
              paddingBottom: '2px',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => e.target.style.borderColor = themeColors.accent}
            onMouseLeave={(e) => e.target.style.borderColor = themeColors.accent + '40'}
          >
            Contact our team
          </a>
        </div>
      </main>

      {/* =================================================================
          CHECKOUT CONFIRMATION MODAL (free → paid)
          ================================================================= */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, animation: 'fadeIn 0.3s ease',
        }}>
          <div style={{
            background: isLightTheme(currentTier) ? '#FFFFFF' : '#1A1A1A', borderRadius: '16px', padding: '40px',
            maxWidth: '440px', width: '90%', textAlign: 'center', animation: 'scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            border: `1px solid ${isLightTheme(currentTier) ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'}`,
          }}>
            <div style={{
              width: '64px', height: '64px', borderRadius: '50%', background: TIER_COLORS[pendingTier]?.accent + '20',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px',
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke={TIER_COLORS[pendingTier]?.accent} strokeWidth="1.5"/>
                <path d="M2 17L12 22L22 17" stroke={TIER_COLORS[pendingTier]?.accent} strokeWidth="1.5"/>
                <path d="M2 12L12 17L22 12" stroke={TIER_COLORS[pendingTier]?.accent} strokeWidth="1.5"/>
              </svg>
            </div>
            <h3 style={{ fontSize: '24px', fontWeight: '400', color: themeColors.text, marginBottom: '12px' }}>
              Confirm upgrade
            </h3>
            <p style={{ fontSize: '15px', color: themeColors.muted, fontFamily: '"DM Sans", sans-serif', marginBottom: '32px', lineHeight: 1.6 }}>
              You'll get instant access to all {TIERS[pendingTier]?.name} features for {prices?.[pendingTier] ? formatAmount(prices[pendingTier].amount, prices[pendingTier].currency) : '...'}/{prices?.[pendingTier]?.interval || 'month'}.
            </p>
            {error && <p style={{ fontSize: '13px', color: '#E24B4A', fontFamily: '"DM Sans", sans-serif', marginBottom: '16px' }}>{error}</p>}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowConfirmModal(false)} disabled={isProcessing}
                style={{ flex: 1, padding: '14px', fontSize: '14px', fontWeight: '500', fontFamily: '"DM Sans", sans-serif', background: 'transparent', border: `1px solid ${themeColors.muted}40`, borderRadius: '8px', color: themeColors.text, cursor: 'pointer', transition: 'all 0.2s ease' }}>
                Cancel
              </button>
              <button onClick={confirmChange} disabled={isProcessing}
                style={{ flex: 1, padding: '14px', fontSize: '14px', fontWeight: '500', fontFamily: '"DM Sans", sans-serif', background: TIER_COLORS[pendingTier]?.accent, border: 'none', borderRadius: '8px', color: '#1A1A1A', cursor: isProcessing ? 'wait' : 'pointer', transition: 'all 0.2s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                {isProcessing ? (<><span style={{ width: '16px', height: '16px', border: '2px solid transparent', borderTopColor: 'currentColor', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />Processing</>) : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =================================================================
          CANCEL CONFIRMATION MODAL (CP4)
          ================================================================= */}
      {showCancelModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, animation: 'fadeIn 0.3s ease',
        }}>
          <div style={{
            background: isLightTheme(currentTier) ? '#FFFFFF' : '#1A1A1A', borderRadius: '16px', padding: '40px',
            maxWidth: '440px', width: '90%', textAlign: 'center', animation: 'scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            border: `1px solid ${isLightTheme(currentTier) ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'}`,
          }}>
            <div style={{
              width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(226, 75, 74, 0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px',
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#E24B4A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 8v4M12 16h.01"/>
              </svg>
            </div>
            <h3 style={{ fontSize: '24px', fontWeight: '400', color: themeColors.text, marginBottom: '12px' }}>
              Downgrade to Free?
            </h3>
            <p style={{ fontSize: '15px', color: themeColors.muted, fontFamily: '"DM Sans", sans-serif', marginBottom: '32px', lineHeight: 1.6 }}>
              Are you sure you want to downgrade? Your {TIERS[currentTier]?.name} plan will remain active until {formatDate(currentPeriodEnd)}.
            </p>
            {cancelError && <p style={{ fontSize: '13px', color: '#E24B4A', fontFamily: '"DM Sans", sans-serif', marginBottom: '16px' }}>{cancelError}</p>}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => { setShowCancelModal(false); setCancelError(''); }} disabled={cancelProcessing}
                style={{ flex: 1, padding: '14px', fontSize: '14px', fontWeight: '500', fontFamily: '"DM Sans", sans-serif', background: 'transparent', border: `1px solid ${themeColors.muted}40`, borderRadius: '8px', color: themeColors.text, cursor: 'pointer', transition: 'all 0.2s ease' }}>
                Keep Plan
              </button>
              <button onClick={handleCancel} disabled={cancelProcessing}
                style={{ flex: 1, padding: '14px', fontSize: '14px', fontWeight: '500', fontFamily: '"DM Sans", sans-serif', background: '#E24B4A', border: 'none', borderRadius: '8px', color: '#FFFFFF', cursor: cancelProcessing ? 'wait' : 'pointer', transition: 'all 0.2s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                {cancelProcessing ? (<><span style={{ width: '16px', height: '16px', border: '2px solid transparent', borderTopColor: 'currentColor', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />Downgrading...</>) : 'Downgrade'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =================================================================
          DOWNGRADE CONFIRMATION MODAL (Pro+ → Pro schedule)
          ================================================================= */}
      {showDowngradeModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, animation: 'fadeIn 0.3s ease',
        }}>
          <div style={{
            background: isLightTheme(currentTier) ? '#FFFFFF' : '#1A1A1A', borderRadius: '16px', padding: '40px',
            maxWidth: '440px', width: '90%', textAlign: 'center', animation: 'scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            border: `1px solid ${isLightTheme(currentTier) ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'}`,
          }}>
            <div style={{
              width: '64px', height: '64px', borderRadius: '50%', background: themeColors.accent + '15',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px',
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={themeColors.accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7L12 12L22 7L12 2Z"/>
                <path d="M2 17L12 22L22 17"/>
                <path d="M2 12L12 17L22 12"/>
              </svg>
            </div>
            <h3 style={{ fontSize: '24px', fontWeight: '400', color: themeColors.text, marginBottom: '12px' }}>
              Downgrade to {TIERS[pendingTier]?.name}?
            </h3>
            <p style={{ fontSize: '15px', color: themeColors.muted, fontFamily: '"DM Sans", sans-serif', marginBottom: '32px', lineHeight: 1.6 }}>
              Your {TIERS[currentTier]?.name} plan will remain active until {formatDate(currentPeriodEnd)}. After that, you'll be switched to {TIERS[pendingTier]?.name} automatically.
            </p>
            {downgradeError && <p style={{ fontSize: '13px', color: '#E24B4A', fontFamily: '"DM Sans", sans-serif', marginBottom: '16px' }}>{downgradeError}</p>}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => { setShowDowngradeModal(false); setDowngradeError(''); setResumeBeforeDowngrade(false); }} disabled={downgradeProcessing}
                style={{ flex: 1, padding: '14px', fontSize: '14px', fontWeight: '500', fontFamily: '"DM Sans", sans-serif', background: 'transparent', border: `1px solid ${themeColors.muted}40`, borderRadius: '8px', color: themeColors.text, cursor: 'pointer', transition: 'all 0.2s ease' }}>
                Keep {TIERS[currentTier]?.name}
              </button>
              <button onClick={handleDowngrade} disabled={downgradeProcessing}
                style={{ flex: 1, padding: '14px', fontSize: '14px', fontWeight: '500', fontFamily: '"DM Sans", sans-serif', background: themeColors.accent, border: 'none', borderRadius: '8px', color: '#1A1A1A', cursor: downgradeProcessing ? 'wait' : 'pointer', transition: 'all 0.2s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                {downgradeProcessing ? (<><span style={{ width: '16px', height: '16px', border: '2px solid transparent', borderTopColor: 'currentColor', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />Scheduling...</>) : 'Confirm Downgrade'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =================================================================
          RESUME CONFIRMATION MODAL
          ================================================================= */}
      {showResumeModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, animation: 'fadeIn 0.3s ease',
        }}>
          <div style={{
            background: isLightTheme(currentTier) ? '#FFFFFF' : '#1A1A1A', borderRadius: '16px', padding: '40px',
            maxWidth: '440px', width: '90%', textAlign: 'center', animation: 'scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            border: `1px solid ${isLightTheme(currentTier) ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'}`,
          }}>
            <div style={{
              width: '64px', height: '64px', borderRadius: '50%', background: themeColors.accent + '15',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px',
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={themeColors.accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12a9 9 0 11-6.22-8.56"/>
                <polyline points="21 3 21 9 15 9"/>
              </svg>
            </div>
            <h3 style={{ fontSize: '24px', fontWeight: '400', color: themeColors.text, marginBottom: '12px' }}>
              Resume subscription?
            </h3>
            <p style={{ fontSize: '15px', color: themeColors.muted, fontFamily: '"DM Sans", sans-serif', marginBottom: '32px', lineHeight: 1.6 }}>
              Your {TIERS[currentTier]?.name} subscription will resume and you'll be charged on {formatDate(currentPeriodEnd)}.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowResumeModal(false)} disabled={resumeProcessing}
                style={{ flex: 1, padding: '14px', fontSize: '14px', fontWeight: '500', fontFamily: '"DM Sans", sans-serif', background: 'transparent', border: `1px solid ${themeColors.muted}40`, borderRadius: '8px', color: themeColors.text, cursor: 'pointer', transition: 'all 0.2s ease' }}>
                Cancel
              </button>
              <button onClick={async () => { await handleResume(); setShowResumeModal(false); }} disabled={resumeProcessing}
                style={{ flex: 1, padding: '14px', fontSize: '14px', fontWeight: '500', fontFamily: '"DM Sans", sans-serif', background: themeColors.accent, border: 'none', borderRadius: '8px', color: '#1A1A1A', cursor: resumeProcessing ? 'wait' : 'pointer', transition: 'all 0.2s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                {resumeProcessing ? (<><span style={{ width: '16px', height: '16px', border: '2px solid transparent', borderTopColor: 'currentColor', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />Resuming...</>) : 'Resume'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =================================================================
          PRORATION PREVIEW MODAL (CP5 — paid → different paid)
          ================================================================= */}
      {showProrationModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, animation: 'fadeIn 0.3s ease',
        }}>
          <div style={{
            background: isLightTheme(currentTier) ? '#FFFFFF' : '#1A1A1A', borderRadius: '16px', padding: '40px',
            maxWidth: '440px', width: '90%', textAlign: 'center', animation: 'scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            border: `1px solid ${isLightTheme(currentTier) ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'}`,
          }}>
            <div style={{
              width: '64px', height: '64px', borderRadius: '50%', background: TIER_COLORS[pendingTier]?.accent + '20',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px',
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke={TIER_COLORS[pendingTier]?.accent} strokeWidth="1.5"/>
                <path d="M2 17L12 22L22 17" stroke={TIER_COLORS[pendingTier]?.accent} strokeWidth="1.5"/>
                <path d="M2 12L12 17L22 12" stroke={TIER_COLORS[pendingTier]?.accent} strokeWidth="1.5"/>
              </svg>
            </div>
            <h3 style={{ fontSize: '24px', fontWeight: '400', color: themeColors.text, marginBottom: '12px' }}>
              {pendingTier && tierRank(pendingTier) > tierRank(currentTier) ? 'Upgrade' : 'Switch'} to {TIERS[pendingTier]?.name}
            </h3>

            {prorationLoading ? (
              <div style={{ padding: '32px 0' }}>
                <div style={{ width: '32px', height: '32px', border: '2px solid rgba(128,128,128,0.2)', borderTopColor: themeColors.accent, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
                <p style={{ fontSize: '14px', color: themeColors.muted, fontFamily: '"DM Sans", sans-serif' }}>Calculating cost...</p>
              </div>
            ) : scheduledChangeBlocked ? (
              <div style={{ marginBottom: '24px' }}>
                <p style={{ fontSize: '15px', color: themeColors.text, fontFamily: '"DM Sans", sans-serif', lineHeight: 1.6, marginBottom: '16px' }}>
                  You have a downgrade scheduled. Cancel it first to upgrade.
                </p>
                <button type="button" onClick={handleCancelDowngradeAndContinue}
                  style={{ padding: '12px 18px', fontSize: '14px', fontWeight: '500', fontFamily: '"DM Sans", sans-serif', background: 'transparent', border: `1px solid ${themeColors.muted}60`, borderRadius: '8px', color: themeColors.text, cursor: 'pointer' }}>
                  Cancel downgrade and continue
                </button>
              </div>
            ) : prorationData ? (
              <div style={{ marginBottom: '32px' }}>
                {prorationNotice && (
                  <p style={{ fontSize: '13px', color: themeColors.text, fontFamily: '"DM Sans", sans-serif', marginBottom: '12px' }}>{prorationNotice}</p>
                )}
                <div style={{ padding: '20px', borderRadius: '12px', background: isLightTheme(currentTier) ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.04)', marginBottom: '16px' }}>
                  {prorationData.credit > 0 ? (
                    <p style={{ fontSize: '16px', color: themeColors.text, fontFamily: '"DM Sans", sans-serif', lineHeight: 1.6 }}>
                      You'll receive a <strong>{formatAmount(prorationData.credit, prorationData.currency)}</strong> credit applied to your next invoice.
                    </p>
                  ) : (
                    <p style={{ fontSize: '16px', color: themeColors.text, fontFamily: '"DM Sans", sans-serif', lineHeight: 1.6 }}>
                      You'll be charged <strong>{formatAmount(prorationData.amountDue, prorationData.currency)}</strong> today.
                    </p>
                  )}
                </div>
                <p style={{ fontSize: '13px', color: themeColors.muted, fontFamily: '"DM Sans", sans-serif' }}>
                  Next billing date: {formatDate(prorationData.nextBillingDate)}
                </p>
              </div>
            ) : null}

            {prorationError && <p style={{ fontSize: '13px', color: '#E24B4A', fontFamily: '"DM Sans", sans-serif', marginBottom: '16px' }}>{prorationError}</p>}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => { setShowProrationModal(false); setProrationData(null); setProrationError(''); setProrationNotice(''); setScheduledChangeBlocked(false); }} disabled={planChangeProcessing}
                style={{ flex: 1, padding: '14px', fontSize: '14px', fontWeight: '500', fontFamily: '"DM Sans", sans-serif', background: 'transparent', border: `1px solid ${themeColors.muted}40`, borderRadius: '8px', color: themeColors.text, cursor: 'pointer', transition: 'all 0.2s ease' }}>
                Cancel
              </button>
              <button onClick={handlePlanChange} disabled={planChangeProcessing || prorationLoading || !!prorationError || !prorationData}
                style={{ flex: 1, padding: '14px', fontSize: '14px', fontWeight: '500', fontFamily: '"DM Sans", sans-serif', background: TIER_COLORS[pendingTier]?.accent, border: 'none', borderRadius: '8px', color: '#1A1A1A', cursor: (planChangeProcessing || prorationLoading || !!prorationError || !prorationData) ? 'not-allowed' : 'pointer', transition: 'all 0.2s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', opacity: (prorationLoading || !!prorationError || !prorationData) ? 0.5 : 1 }}>
                {planChangeProcessing ? (<><span style={{ width: '16px', height: '16px', border: '2px solid transparent', borderTopColor: 'currentColor', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />Processing</>) : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =================================================================
          EMBEDDED CHECKOUT OVERLAY (CP3 — free → paid)
          ================================================================= */}
      {checkoutClientSecret && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(12px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, animation: 'fadeIn 0.3s ease',
        }}>
          <div style={{
            background: '#FFFFFF', borderRadius: '16px', width: '90%', maxWidth: '600px', maxHeight: '90vh',
            overflow: 'auto', position: 'relative', animation: 'scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}>
            <button onClick={handleCloseCheckout}
              style={{ position: 'sticky', top: '16px', float: 'right', marginRight: '16px', width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(0, 0, 0, 0.05)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, transition: 'background 0.2s ease' }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)'}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div style={{ padding: '24px 32px 32px' }}>
              <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret: checkoutClientSecret }}>
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          </div>
        </div>
      )}

      {/* =================================================================
          PAYMENT METHOD MODAL
          ================================================================= */}
      {showPaymentModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, animation: 'fadeIn 0.3s ease',
        }}>
          <div style={{
            background: isLightTheme(currentTier) ? '#FFFFFF' : '#1A1A1A', borderRadius: '20px',
            maxWidth: '480px', width: '90%', animation: 'scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            border: `1px solid ${isLightTheme(currentTier) ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'}`,
            maxHeight: '90vh', overflow: 'auto',
            boxShadow: '0 24px 80px rgba(0,0,0,0.3)',
          }}>
            {/* Header */}
            <div style={{ padding: '28px 32px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: '300', color: themeColors.text, fontFamily: '"Cormorant Garamond", Georgia, serif', letterSpacing: '-0.01em' }}>
                  Payment Methods
                </h3>
                <p style={{ fontSize: '13px', color: themeColors.muted, fontFamily: '"DM Sans", sans-serif', marginTop: '4px' }}>
                  Manage your cards on file
                </p>
              </div>
              <button onClick={() => { setShowPaymentModal(false); setShowAddCard(false); setSetupClientSecret(null); setPaymentMethodError(''); }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: '6px',
                  color: themeColors.muted, transition: 'color 0.2s ease', marginTop: '-2px',
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = themeColors.text}
                onMouseLeave={(e) => e.currentTarget.style.color = themeColors.muted}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>

            {/* Divider */}
            <div style={{ margin: '20px 32px 0', height: '1px', background: `linear-gradient(90deg, transparent, ${themeColors.muted}20, transparent)` }} />

            {/* Content */}
            <div style={{ padding: '24px 32px 32px' }}>
              {paymentMethodError && (
                <div style={{
                  fontSize: '13px', color: '#E24B4A', fontFamily: '"DM Sans", sans-serif',
                  padding: '10px 14px', borderRadius: '8px', background: 'rgba(226, 75, 74, 0.08)',
                  marginBottom: '20px',
                }}>
                  {paymentMethodError}
                </div>
              )}

              {/* Card List */}
              {paymentMethodsLoading ? (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                  <div style={{ width: '24px', height: '24px', border: '2px solid rgba(128,128,128,0.15)', borderTopColor: themeColors.accent, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
                  <p style={{ fontSize: '13px', color: themeColors.muted, fontFamily: '"DM Sans", sans-serif' }}>Loading cards...</p>
                </div>
              ) : (
                <div style={{ marginBottom: '24px' }}>
                  {paymentMethods.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '28px 0' }}>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={themeColors.muted + '40'} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '12px' }}>
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                        <line x1="1" y1="10" x2="23" y2="10"/>
                      </svg>
                      <p style={{ fontSize: '14px', color: themeColors.muted, fontFamily: '"DM Sans", sans-serif' }}>
                        No payment methods on file
                      </p>
                    </div>
                  ) : paymentMethods.map((pm, idx) => (
                    <div key={pm.id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '16px 18px', borderRadius: '12px', marginBottom: idx < paymentMethods.length - 1 ? '8px' : 0,
                      background: pm.isDefault
                        ? (themeColors.text === '#F5F5F0' ? 'rgba(201,169,98,0.08)' : 'rgba(201,169,98,0.06)')
                        : (themeColors.text === '#F5F5F0' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'),
                      border: `1px solid ${pm.isDefault ? themeColors.accent + '30' : 'transparent'}`,
                      transition: 'all 0.2s ease',
                    }}>
                      {/* Card Info */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
                        <div style={{
                          width: '40px', height: '28px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: themeColors.text === '#F5F5F0' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                          border: `1px solid ${themeColors.muted}15`,
                          flexShrink: 0,
                        }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={pm.isDefault ? themeColors.accent : themeColors.muted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                            <line x1="1" y1="10" x2="23" y2="10"/>
                          </svg>
                        </div>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '500', color: themeColors.text, fontFamily: '"DM Sans", sans-serif', textTransform: 'capitalize' }}>
                              {pm.brand}
                            </span>
                            <span style={{ fontSize: '14px', color: themeColors.muted, fontFamily: '"DM Sans", sans-serif', letterSpacing: '0.05em' }}>
                              ····{pm.last4}
                            </span>
                            {pm.isDefault && (
                              <span style={{
                                fontSize: '10px', fontWeight: '600', color: themeColors.accent, fontFamily: '"DM Sans", sans-serif',
                                textTransform: 'uppercase', letterSpacing: '0.08em',
                                padding: '2px 8px', borderRadius: '10px', background: themeColors.accent + '15',
                              }}>
                                Default
                              </span>
                            )}
                          </div>
                          <span style={{ fontSize: '12px', color: themeColors.muted + 'A0', fontFamily: '"DM Sans", sans-serif', marginTop: '2px', display: 'block' }}>
                            Expires {pm.expMonth}/{pm.expYear}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      {!pm.isDefault && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                          <button onClick={() => handleSetDefault(pm.id)}
                            style={{
                              fontSize: '12px', fontWeight: '500', fontFamily: '"DM Sans", sans-serif',
                              color: themeColors.accent, background: 'none', border: 'none',
                              cursor: 'pointer', padding: '6px 10px', borderRadius: '6px',
                              transition: 'background 0.2s ease',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = themeColors.accent + '15'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                          >
                            Set Default
                          </button>
                          <button onClick={() => handleRemoveCard(pm.id)}
                            style={{
                              fontSize: '12px', fontWeight: '500', fontFamily: '"DM Sans", sans-serif',
                              color: themeColors.muted + '80', background: 'none', border: 'none',
                              cursor: 'pointer', padding: '6px 10px', borderRadius: '6px',
                              transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#E24B4A'; e.currentTarget.style.background = 'rgba(226,75,74,0.08)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = themeColors.muted + '80'; e.currentTarget.style.background = 'none'; }}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Add Card Section */}
              {showAddCard && setupClientSecret ? (
                <div style={{
                  padding: '24px', borderRadius: '14px',
                  background: themeColors.text === '#F5F5F0' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.02)',
                  border: `1px solid ${themeColors.muted}15`,
                }}>
                  <Elements stripe={stripePromise} options={{ appearance: { theme: isLightTheme(currentTier) ? 'stripe' : 'night' } }}>
                    <AddCardForm
                      clientSecret={setupClientSecret}
                      onSuccess={handleCardAdded}
                      onCancel={() => { setShowAddCard(false); setSetupClientSecret(null); }}
                      themeColors={themeColors}
                    />
                  </Elements>
                </div>
              ) : (
                <button onClick={startAddCard}
                  style={{
                    width: '100%', padding: '15px', fontSize: '13px', fontWeight: '500', fontFamily: '"DM Sans", sans-serif',
                    letterSpacing: '0.04em', background: 'transparent', color: themeColors.muted,
                    border: `1px dashed ${themeColors.muted}25`, borderRadius: '12px',
                    cursor: 'pointer', transition: 'all 0.25s ease',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = themeColors.accent + '60'; e.currentTarget.style.color = themeColors.accent; e.currentTarget.style.background = themeColors.accent + '06'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = themeColors.muted + '25'; e.currentTarget.style.color = themeColors.muted; e.currentTarget.style.background = 'transparent'; }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Add New Card
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* =================================================================
          COMPONENT-SPECIFIC STYLES
          ================================================================= */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,400&family=DM+Sans:wght@400;500;600&display=swap');
        
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        * { box-sizing: border-box; margin: 0; }
      `}</style>
    </div>
  );
}
